import { useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { addBusinessDays, addDays, isAfter, isBefore } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { makeAPIRequestReactQuery } from '@/react-query/api';
import { endpoints } from '@/react-query/endpoints';
import { usePreferences } from '@/react-query/usePreferences';

import { mockAIImpactDataAtom } from './atoms';
import type {
  HighlightCategory,
  PracticeAIImpactData,
  PracticeAIImpactDataResponse,
  TrialStatusData,
} from './types';
import { TrialStatus } from './types';

/**
 * Generates a short 8-character hash from an object.
 * Used to create unique query keys that invalidate when mock data changes.
 */
const hashObject = (obj: unknown): string => {
  const str = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to positive number and then to base36 string, take first 8 chars
  return Math.abs(hash).toString(36).slice(0, 8).padStart(8, '0');
};

export const useGetTrialStatus = () => {
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  return useQuery({
    queryKey: [
      endpoints.reverseTrial,
      primaryOrganizationId,
      primaryPracticeId,
      endpoints.status,
    ],
    queryFn: async () => {
      return await makeAPIRequestReactQuery<TrialStatusData>(
        `/${endpoints.reverseTrial}/${primaryOrganizationId}/${primaryPracticeId}/${endpoints.status}`,
      );
    },
    enabled: !!primaryPracticeId,
  });
};

interface UpdateTrialStatusVariables {
  status: TrialStatus;
  metadata?: Record<string, any>;
}

export const useUpdateTrialStatus = () => {
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  return useMutation<
    void,
    AxiosError<{ status: number; description: string }>,
    UpdateTrialStatusVariables,
    unknown
  >({
    mutationFn: async (variables) => {
      return await makeAPIRequestReactQuery(
        `/${endpoints.reverseTrial}/${primaryOrganizationId}/${primaryPracticeId}/${endpoints.status}`,
        {},
        'PATCH',
        {
          data: {
            ...variables,
          },
        },
      );
    },
  });
};

const convertToNumber = (value?: string) => {
  return !value || isNaN(parseFloat(value)) ? undefined : Number(value);
};

const transformAIImpactResponse = (
  response: PracticeAIImpactDataResponse,
): PracticeAIImpactData => {
  return {
    totalUniquePatients: convertToNumber(response.totalUniquePatients),
    uniquePatientsEducatedWithAi: convertToNumber(
      response.uniquePatientsEducatedWithAi,
    ),
    uniquePatientsWithRecs: convertToNumber(response.uniquePatientsWithRecs),
    uniqueCompletedTxWithRec: convertToNumber(
      response.uniqueCompletedTxWithRec,
    ),
    uniquePlannedTxWithRec: convertToNumber(response.uniquePlannedTxWithRec),
    uniqueScheduledTxWithRec: convertToNumber(
      response.uniqueScheduledTxWithRec,
    ),
    avgTxPlanValuePerPatientPostAi: convertToNumber(
      response.avgTxPlanValuePerPatientPostAi,
    ),
    avgTxPlanValuePerPatientPreAi: convertToNumber(
      response.avgTxPlanValuePerPatientPreAi,
    ),
    highlightCategory: response.highlightCategory as HighlightCategory,
    fillingPostAiValue: convertToNumber(response.fillingPostAiValue),
    fillingPreAiValue: convertToNumber(response.fillingPreAiValue),
    crownPostAiValue: convertToNumber(response.crownPostAiValue),
    crownPreAiValue: convertToNumber(response.crownPreAiValue),
    srpPostAiValue: convertToNumber(response.srpPostAiValue),
    srpPreAiValue: convertToNumber(response.srpPreAiValue),
    extractionPostAiValue: convertToNumber(response.extractionPostAiValue),
    extractionPreAiValue: convertToNumber(response.extractionPreAiValue),
    incipientPostAiValue: convertToNumber(response.incipientPostAiValue),
    incipientPreAiValue: convertToNumber(response.incipientPreAiValue),
    implantPostAiValue: convertToNumber(response.implantPostAiValue),
    implantPreAiValue: convertToNumber(response.implantPreAiValue),
    rootCanalPostAiValue: convertToNumber(response.rootCanalPostAiValue),
    rootCanalPreAiValue: convertToNumber(response.rootCanalPreAiValue),
  };
};

export const useGetPracticeAIImpact = (options?: { isEnabled?: boolean }) => {
  const { isEnabled } = options ?? {};
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;
  const mockData = useAtomValue(mockAIImpactDataAtom);

  // Generate a hash of mock data to ensure query key changes when mock data changes
  const mockDataHash = useMemo(
    () => (mockData ? hashObject(mockData) : null),
    [mockData],
  );

  return useQuery({
    queryKey: [
      endpoints.practiceAIImpact,
      primaryOrganizationId,
      primaryPracticeId,
      mockDataHash ?? 'api',
    ],
    queryFn: async (): Promise<PracticeAIImpactData> => {
      // If mock data exists, transform it
      if (mockData) {
        return transformAIImpactResponse(mockData);
      }

      // Otherwise fetch from API and transform
      const response =
        await makeAPIRequestReactQuery<PracticeAIImpactDataResponse>(
          `${endpoints.videaInsights}/${primaryOrganizationId}/${primaryPracticeId}/${endpoints.practiceAIImpact}`,
        );
      return transformAIImpactResponse(response);
    },
    enabled:
      (isEnabled !== undefined ? isEnabled : true) &&
      !!primaryPracticeId &&
      !!primaryOrganizationId,
  });
};

const getIsWithinThreeBusinessDays = (startDate: string) => {
  const start = new Date(startDate);
  const end = addBusinessDays(start, 3);
  return isBefore(new Date(), end);
};

export const useIsWithinThreeBusinessDaysOfStart = (
  trialStatusData?: TrialStatusData,
) => {
  const isReverseTrialPractice = trialStatusData?.isReverseTrialPractice;
  const trialStartedOn = trialStatusData?.trialStartedOn;
  const paidStartedOn = trialStatusData?.paidStartedOn;
  const paidEndedOn = trialStatusData?.paidEndedOn;

  const isOnboardingTrial =
    trialStatusData?.status === TrialStatus.TRIAL &&
    !!trialStartedOn &&
    getIsWithinThreeBusinessDays(trialStartedOn);
  const isOnboardingPaid =
    !!paidStartedOn &&
    !paidEndedOn &&
    !isReverseTrialPractice && // Don't show alert if it was already shown during the trial
    getIsWithinThreeBusinessDays(paidStartedOn);

  return isOnboardingTrial || isOnboardingPaid;
};

const getIsAfterOneBusinessDay = (startDate: string) => {
  const start = new Date(startDate);
  const end = addBusinessDays(start, 1);
  return isAfter(new Date(), end);
};

export const useIsOneBusinessDayAfterStart = (
  trialStatusData?: TrialStatusData,
) => {
  const trialStartedOn = trialStatusData?.trialStartedOn;
  const paidStartedOn = trialStatusData?.paidStartedOn;
  const paidEndedOn = trialStatusData?.paidEndedOn;

  const isOneBusinessDayAfterTrialStart =
    trialStatusData?.status === TrialStatus.TRIAL &&
    !!trialStartedOn &&
    getIsAfterOneBusinessDay(trialStartedOn);
  const isOneBusinessDayAfterPaidStart =
    !!paidStartedOn && !paidEndedOn && getIsAfterOneBusinessDay(paidStartedOn);

  return isOneBusinessDayAfterTrialStart || isOneBusinessDayAfterPaidStart;
};

export const useIsFirstWeekOfTrial = (trialStatusData?: TrialStatusData) => {
  const trialStartedOn = trialStatusData?.trialStartedOn;

  return (
    trialStatusData?.status === TrialStatus.TRIAL &&
    !!trialStartedOn &&
    isBefore(new Date(), addDays(new Date(trialStartedOn), 7))
  );
};

export const useIsAfterAIImpactDataCollectionPeriod = (
  trialStatusData?: TrialStatusData,
) => {
  const paidStartedOn = trialStatusData?.paidStartedOn;
  const trialStartedOn = trialStatusData?.trialStartedOn;
  const trialLengthInDays = trialStatusData?.trialLengthInDays;
  const isAbridgedTrial = !!trialLengthInDays && trialLengthInDays < 30;

  const tenDaysAfterTrialStart = trialStartedOn
    ? addDays(new Date(trialStartedOn), 10)
    : null;
  const twoWeeksAfterTrialStart = trialStartedOn
    ? addDays(new Date(trialStartedOn), 14)
    : null;
  const twoWeeksAfterPaidStart = paidStartedOn
    ? addDays(new Date(paidStartedOn), 14)
    : null;
  const isTenDaysAfterTrialStart =
    !!tenDaysAfterTrialStart && isAfter(new Date(), tenDaysAfterTrialStart);
  const isTwoWeeksAfterTrialStart =
    !!twoWeeksAfterTrialStart && isAfter(new Date(), twoWeeksAfterTrialStart);
  const isTwoWeeksAfterPaidStart =
    !!twoWeeksAfterPaidStart && isAfter(new Date(), twoWeeksAfterPaidStart);

  if (trialStatusData?.status === TrialStatus.TRIAL) {
    return isAbridgedTrial
      ? isTenDaysAfterTrialStart
      : isTwoWeeksAfterTrialStart;
  }

  return trialStatusData?.isReverseTrialPractice
    ? isTwoWeeksAfterTrialStart
    : isTwoWeeksAfterPaidStart;
};

export const usePracticeAIImpactSummary = (
  practiceAIImpactData?: PracticeAIImpactData,
) => {
  const { t } = useLingui();
  const { data: prefs } = usePreferences();

  const patientEducationDetails = useMemo(() => {
    const details = [];
    if (practiceAIImpactData?.totalUniquePatients) {
      details.push({
        value: practiceAIImpactData.totalUniquePatients,
        label: t`Patients seen`,
      });
    }
    if (practiceAIImpactData?.uniquePatientsWithRecs) {
      details.push({
        value: practiceAIImpactData.uniquePatientsWithRecs,
        label: t`Patients needing treatment`,
      });
    }
    if (practiceAIImpactData?.uniquePatientsEducatedWithAi) {
      // Cap patients educated by AI to patients needing treatment if it exceeds
      const educatedValue = practiceAIImpactData.uniquePatientsWithRecs
        ? Math.min(
            practiceAIImpactData.uniquePatientsEducatedWithAi,
            practiceAIImpactData.uniquePatientsWithRecs,
          )
        : practiceAIImpactData.uniquePatientsEducatedWithAi;
      details.push({
        value: educatedValue,
        label: t`Patients educated with AI`,
      });
    }
    return details;
  }, [practiceAIImpactData, t]);

  const caseAcceptanceDetails = useMemo(() => {
    const details = [];
    if (practiceAIImpactData?.uniquePlannedTxWithRec) {
      details.push({
        value: practiceAIImpactData.uniquePlannedTxWithRec,
        label: t`Treatments planned with AI`,
      });
    }
    if (practiceAIImpactData?.uniqueScheduledTxWithRec) {
      details.push({
        value: practiceAIImpactData.uniqueScheduledTxWithRec,
        label: t`Treatments scheduled`,
      });
    }
    if (practiceAIImpactData?.uniqueCompletedTxWithRec) {
      details.push({
        value: practiceAIImpactData.uniqueCompletedTxWithRec,
        label: t`Treatments completed`,
      });
    }
    return details;
  }, [practiceAIImpactData, t]);

  const patientEducationRate = useMemo(() => {
    if (
      !practiceAIImpactData?.totalUniquePatients ||
      !practiceAIImpactData?.uniquePatientsEducatedWithAi
    ) {
      return null;
    }
    const rate = Math.round(
      (practiceAIImpactData.uniquePatientsEducatedWithAi /
        practiceAIImpactData.totalUniquePatients) *
        100,
    );
    // If for some reason the rate is greater than 100, hide the metric
    return rate > 100 ? null : rate;
  }, [
    practiceAIImpactData?.totalUniquePatients,
    practiceAIImpactData?.uniquePatientsEducatedWithAi,
  ]);

  const caseAcceptanceRate = useMemo(() => {
    if (
      !practiceAIImpactData?.uniquePlannedTxWithRec ||
      !practiceAIImpactData?.uniqueScheduledTxWithRec
    ) {
      return null;
    }
    const rate = Math.round(
      (practiceAIImpactData.uniqueScheduledTxWithRec /
        practiceAIImpactData.uniquePlannedTxWithRec) *
        100,
    );
    // If for some reason the rate is greater than 100, hide the metric
    return rate > 100 ? null : rate;
  }, [
    practiceAIImpactData?.uniquePlannedTxWithRec,
    practiceAIImpactData?.uniqueScheduledTxWithRec,
  ]);

  // Calculate rate for highlightCategory (backend-determined best category) and overall, then select the best
  const bestTreatmentPlanData = useMemo(() => {
    interface RateData {
      rate: number;
      category: HighlightCategory | 'Overall';
      preAiValue: number;
      postAiValue: number;
    }

    let highlightPreAiValue: number | undefined;
    let highlightPostAiValue: number | undefined;

    if (practiceAIImpactData?.highlightCategory) {
      switch (practiceAIImpactData.highlightCategory) {
        case 'Fillings':
          highlightPreAiValue = practiceAIImpactData?.fillingPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.fillingPostAiValue;
          break;
        case 'Crowns':
          highlightPreAiValue = practiceAIImpactData?.crownPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.crownPostAiValue;
          break;
        case 'SRP':
          highlightPreAiValue = practiceAIImpactData?.srpPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.srpPostAiValue;
          break;
        case 'Extraction':
          highlightPreAiValue = practiceAIImpactData?.extractionPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.extractionPostAiValue;
          break;
        case 'Incipient':
          highlightPreAiValue = practiceAIImpactData?.incipientPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.incipientPostAiValue;
          break;
        case 'Implant':
          highlightPreAiValue = practiceAIImpactData?.implantPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.implantPostAiValue;
          break;
        case 'Root Canal':
          highlightPreAiValue = practiceAIImpactData?.rootCanalPreAiValue;
          highlightPostAiValue = practiceAIImpactData?.rootCanalPostAiValue;
          break;
        default:
          break;
      }
    }

    let highlightRate: RateData | null = null;
    if (
      highlightPreAiValue &&
      highlightPostAiValue &&
      practiceAIImpactData?.highlightCategory
    ) {
      const valueIncrease = highlightPostAiValue - highlightPreAiValue;
      const rate = Math.round((valueIncrease / highlightPreAiValue) * 100);
      highlightRate = {
        rate,
        category: practiceAIImpactData.highlightCategory,
        preAiValue: highlightPreAiValue,
        postAiValue: highlightPostAiValue,
      };
    }

    let overallRate: RateData | null = null;
    if (
      practiceAIImpactData?.avgTxPlanValuePerPatientPreAi &&
      practiceAIImpactData?.avgTxPlanValuePerPatientPostAi
    ) {
      const valueIncrease =
        practiceAIImpactData.avgTxPlanValuePerPatientPostAi -
        practiceAIImpactData.avgTxPlanValuePerPatientPreAi;
      const rate = Math.round(
        (valueIncrease / practiceAIImpactData.avgTxPlanValuePerPatientPreAi) *
          100,
      );
      overallRate = {
        rate,
        category: 'Overall',
        preAiValue: practiceAIImpactData.avgTxPlanValuePerPatientPreAi,
        postAiValue: practiceAIImpactData.avgTxPlanValuePerPatientPostAi,
      };
    }

    // Compare the two rates and return the best one (most positive)
    if (
      (!highlightRate || highlightRate.rate < 0) &&
      (!overallRate || overallRate.rate < 0)
    ) {
      return null;
    }

    if (!highlightRate || highlightRate.rate < 0) {
      return overallRate && overallRate.rate > 0 ? overallRate : null;
    }

    if (!overallRate || overallRate.rate < 0) {
      return highlightRate.rate > 0 ? highlightRate : null;
    }

    return highlightRate.rate > overallRate.rate ? highlightRate : overallRate;
  }, [
    practiceAIImpactData?.avgTxPlanValuePerPatientPreAi,
    practiceAIImpactData?.avgTxPlanValuePerPatientPostAi,
    practiceAIImpactData?.highlightCategory,
    practiceAIImpactData?.crownPostAiValue,
    practiceAIImpactData?.crownPreAiValue,
    practiceAIImpactData?.extractionPostAiValue,
    practiceAIImpactData?.extractionPreAiValue,
    practiceAIImpactData?.fillingPostAiValue,
    practiceAIImpactData?.fillingPreAiValue,
    practiceAIImpactData?.implantPostAiValue,
    practiceAIImpactData?.implantPreAiValue,
    practiceAIImpactData?.incipientPostAiValue,
    practiceAIImpactData?.incipientPreAiValue,
    practiceAIImpactData?.rootCanalPostAiValue,
    practiceAIImpactData?.rootCanalPreAiValue,
    practiceAIImpactData?.srpPostAiValue,
    practiceAIImpactData?.srpPreAiValue,
  ]);

  const showPatientEducationCard =
    !!patientEducationRate || !!patientEducationDetails.length;
  const showCaseAcceptanceCard =
    !!caseAcceptanceRate || !!caseAcceptanceDetails.length;

  const showTreatmentPlanningCard = !!bestTreatmentPlanData;

  return {
    patientEducationDetails,
    caseAcceptanceDetails,
    patientEducationRate,
    caseAcceptanceRate,
    showPatientEducationCard,
    showCaseAcceptanceCard,
    showTreatmentPlanningCard,
    selectedTreatmentPlanCategory: bestTreatmentPlanData?.category,
    selectedTreatmentPlanPreAiValue: bestTreatmentPlanData?.preAiValue,
    selectedTreatmentPlanPostAiValue: bestTreatmentPlanData?.postAiValue,
    treatmentPlanValueRate: bestTreatmentPlanData?.rate ?? null,
    isPracticeAIImpactAvailable:
      !!prefs?.showAIImpactModal &&
      (showPatientEducationCard ||
        showCaseAcceptanceCard ||
        showTreatmentPlanningCard),
  };
};
